import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getProgram, createProgram, updateProgram, ProgramCreate, ProgramUpdate } from '../../api/data';

const ProgramForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name_ar: '',
    name_en: '',
    faculty: '',
    aliases: ''
  });

  useEffect(() => {
    if (isEdit && id) {
      loadProgram(parseInt(id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadProgram = async (programId: number) => {
    try {
      const program = await getProgram(programId);
      setFormData({
        name_ar: program.name_ar,
        name_en: program.name_en,
        faculty: program.faculty || '',
        aliases: program.aliases.join(', ')
      });
    } catch (error) {
      alert('Failed to load program');
      navigate('/data/programs');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const aliases = formData.aliases
        .split(',')
        .map(a => a.trim())
        .filter(a => a.length > 0);

      if (isEdit && id) {
        const data: ProgramUpdate = {
          name_ar: formData.name_ar,
          name_en: formData.name_en,
          faculty: formData.faculty || undefined,
          aliases
        };
        await updateProgram(parseInt(id), data);
        alert('Program updated successfully');
      } else {
        const data: ProgramCreate = {
          name_ar: formData.name_ar,
          name_en: formData.name_en,
          faculty: formData.faculty || undefined,
          aliases
        };
        await createProgram(data);
        alert('Program created successfully');
      }
      
      navigate('/data/programs');
    } catch (error: any) {
      alert('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
        <div className="card p-12 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-primary-500">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold text-primary-900 tracking-tight leading-tight">
          {isEdit ? 'Edit Program' : 'Create Program'}
        </h1>
        <Link to="/data/programs" className="btn-ghost text-xs gap-1">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="card p-6 space-y-5">
        <div>
          <label className="label">Arabic Name <span className="text-error">*</span></label>
          <input
            type="text"
            value={formData.name_ar}
            onChange={(e) => setFormData({ ...formData, name_ar: e.target.value })}
            required
            className="input-base"
            placeholder="اسم البرنامج بالعربية"
          />
        </div>

        <div>
          <label className="label">English Name <span className="text-error">*</span></label>
          <input
            type="text"
            value={formData.name_en}
            onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
            required
            className="input-base"
            placeholder="Program Name in English"
          />
        </div>

        <div>
          <label className="label">Faculty</label>
          <input
            type="text"
            value={formData.faculty}
            onChange={(e) => setFormData({ ...formData, faculty: e.target.value })}
            className="input-base"
            placeholder="Faculty Name"
          />
        </div>

        <div>
          <label className="label">Aliases (comma-separated)</label>
          <input
            type="text"
            value={formData.aliases}
            onChange={(e) => setFormData({ ...formData, aliases: e.target.value })}
            className="input-base"
            placeholder="Alternative names, separated by commas"
          />
          <p className="mt-1.5 text-xs text-primary-400">Enter alternative names or abbreviations for this program</p>
        </div>

        <div className="flex gap-3 pt-3">
          <button type="submit" disabled={saving} className="btn-primary gap-1.5">
            {saving ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Saving...
              </>
            ) : (isEdit ? 'Update Program' : 'Create Program')}
          </button>
          <Link to="/data/programs" className="btn-secondary">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

export default ProgramForm;
